import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * Button — Capture Sheet fields 12.1 and 22.2.
 *
 * Evidence-backed variants only. The source shows exactly three: a dark filled
 * pill, an outlined pill of identical size sitting directly beneath it, and a
 * small inline pill. The library's stock `outline` / `ghost` / `destructive`
 * variants are deliberately NOT reproduced as-is — `destructive` has no
 * precedent anywhere in 342 screens (sheet 19.8) and is provided only because
 * the token contract requires it to exist.
 *
 * `disabled` is a distinct grey with the label kept white (sheet 6.7) — NOT a
 * reduced opacity of the enabled state, which would tint the label too.
 */
const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap",
    "rounded-pill text-row font-medium",
    "transition-[background-color,border-color,color] duration-200 ease-out-ds",
    "outline-none focus-visible:outline-ring focus-visible:outline-2 focus-visible:outline-offset-2",
    "disabled:pointer-events-none",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  ],
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 disabled:bg-muted-foreground/45 disabled:text-primary-foreground",
        secondary:
          "border border-border bg-transparent text-foreground hover:bg-muted disabled:text-muted-foreground",
        ghost: "text-foreground hover:bg-muted disabled:text-muted-foreground",
        link: "h-auto rounded-none px-0 text-accent underline-offset-4 hover:underline",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:bg-muted-foreground/45",
      },
      size: {
        sm: "h-9 px-4 text-support",
        default: "h-12 px-6",
        block: "h-13 w-full px-6",
        icon: "size-10 px-0",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  children,
  disabled,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    /** Shows an in-button spinner and blocks interaction (sheet 8.4). */
    loading?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      data-loading={loading || undefined}
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={disabled ?? loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? (
        <>
          <Loader2 className="animate-spin" aria-hidden />
          <span className="sr-only">Loading</span>
        </>
      ) : (
        children
      )}
    </Comp>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
